import React from 'react';

const SkeletonBox = ({ width = '100%', height = '40px', radius = '6px', style = {} }) => (
  <div
    style={{
      width,
      height,
      backgroundColor: '#e5e7eb',
      borderRadius: radius,
      animation: 'pulse 1.5s infinite',
      ...style
    }}
  />
);

const FieldSkeleton = ({ controlHeight = 42, multiline = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <SkeletonBox width="55%" height="12px" radius="6px" style={{ backgroundColor: '#e2e8f0' }} />
    <SkeletonBox
      height={multiline ? '80px' : `${controlHeight}px`}
      radius="6px"
      style={{ backgroundColor: '#e5e7eb' }}
    />
  </div>
);

const SectionHeaderSkeleton = () => (
  <div style={{ marginTop: '30px', marginBottom: '20px' }}>
    <SkeletonBox width="180px" height="18px" radius="8px" style={{ backgroundColor: '#e2e8f0' }} />
    <div style={{ height: '2px', backgroundColor: '#e2e8f0', marginTop: '10px' }} />
  </div>
);

const CreatePickupChallanSkeleton = () => (
  <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
    {/* Header toolbar (Back, Title, Actions) */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
      <SkeletonBox width="90px" height="30px" radius="8px" />
      <SkeletonBox width="220px" height="32px" radius="10px" />
      <div style={{ display: 'flex', gap: '10px' }}>
        <SkeletonBox width="120px" height="40px" radius="10px" />
        <SkeletonBox width="130px" height="40px" radius="10px" />
        <SkeletonBox width="110px" height="40px" radius="10px" />
      </div>
    </div>
    
    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
      {/* Top row: Inward Type + Delivered DC Picker */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
        <FieldSkeleton controlHeight={42} />
        <FieldSkeleton controlHeight={42} />
      </div>

      <SectionHeaderSkeleton />

      {/* Basic Details grid (matches current page: 4 columns + 2 span fields) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
        <FieldSkeleton controlHeight={42} />
        <FieldSkeleton controlHeight={42} />
        <FieldSkeleton controlHeight={42} />
        <FieldSkeleton controlHeight={42} />
        <div style={{ gridColumn: 'span 2' }}>
          <FieldSkeleton controlHeight={42} />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <FieldSkeleton controlHeight={42} />
        </div>
      </div>

      <SectionHeaderSkeleton />

      {/* Party Details (two cards) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
        {/* Consignee card */}
        <div style={{ padding: '20px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <SkeletonBox width="260px" height="18px" radius="8px" style={{ backgroundColor: '#e2e8f0', marginBottom: '18px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <FieldSkeleton controlHeight={42} />
            <FieldSkeleton multiline />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <FieldSkeleton controlHeight={42} />
              <FieldSkeleton controlHeight={42} />
            </div>
            <FieldSkeleton controlHeight={42} />
            <FieldSkeleton controlHeight={42} />
          </div>
        </div>

        {/* Pickup Location card */}
        <div style={{ padding: '20px', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
          <SkeletonBox width="220px" height="18px" radius="8px" style={{ backgroundColor: '#e2e8f0', marginBottom: '18px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <FieldSkeleton controlHeight={42} />
            <FieldSkeleton controlHeight={42} />
            <FieldSkeleton multiline />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <FieldSkeleton controlHeight={42} />
              <FieldSkeleton controlHeight={42} />
            </div>
            <FieldSkeleton controlHeight={42} />
            <FieldSkeleton controlHeight={42} />
          </div>
        </div>
      </div>

      {/* General Remarks */}
      <div style={{ marginTop: '20px' }}>
        <FieldSkeleton multiline />
      </div>

      <SectionHeaderSkeleton />

      {/* Components search + table placeholder */}
      <div style={{ width: '100%', maxWidth: '520px', marginBottom: '15px' }}>
        <SkeletonBox width="40%" height="12px" radius="6px" style={{ backgroundColor: '#e2e8f0', marginBottom: '6px' }} />
        <SkeletonBox height="38px" radius="8px" style={{ backgroundColor: '#e5e7eb' }} />
      </div>

      <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ backgroundColor: '#f8fafc', padding: '12px' }}>
          <SkeletonBox width="55%" height="12px" radius="6px" style={{ backgroundColor: '#e2e8f0' }} />
        </div>
        <div style={{ padding: '12px' }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px', padding: '10px 0', borderBottom: i === 5 ? 'none' : '1px solid #f1f5f9' }}>
              <SkeletonBox height="12px" radius="6px" style={{ backgroundColor: '#e5e7eb' }} />
              <SkeletonBox height="12px" radius="6px" style={{ backgroundColor: '#e5e7eb' }} />
              <SkeletonBox height="12px" radius="6px" style={{ backgroundColor: '#e5e7eb' }} />
              <SkeletonBox height="12px" radius="6px" style={{ backgroundColor: '#e5e7eb' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
    
    <style>{`
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    `}</style>
  </div>
);

export default CreatePickupChallanSkeleton;
